/**
 * Window-event bridge (rule #26) for a mic tap that could start no
 * transcription at all: Enhanced Voice is not available for this tap (its
 * runtime fallback is active, or the browser has no recorder path) and the
 * browser has no speech engine, or the browser engine refused to start.
 * useSpeechToText dispatches it at those refusals; VoiceStartUnavailableToast
 * (mounted globally inside VoiceCaptureProvider) says why and what to do next,
 * so the microphone never silently does nothing.
 *
 * Lives in `lib/` rather than beside the toast so the dispatcher and the
 * constant can be imported by both the hook and the component without
 * violating `react-refresh/only-export-components`. Same shape as
 * `voice-auto-stop-events.ts` and `queue-cap-events.ts`.
 */
import type { FallbackReason } from '@/lib/settings-store';

export const VOICE_START_UNAVAILABLE_EVENT = 'adia:voice-start-unavailable';

/**
 * What the explanation may promise:
 * - 'retry-soon': the one-minute service fallback is active, so Enhanced
 *   Voice is tried again on a tap after it expires.
 * - 'offline': the offline fallback is active, so Enhanced Voice returns
 *   when the browser is back online.
 * - 'unavailable': nothing temporary is known; only typing is promised.
 */
export type VoiceStartUnavailableReason = 'retry-soon' | 'offline' | 'unavailable';

/** Promise a return of voice only when Enhanced Voice is open and its fallback is temporary. */
export function voiceStartUnavailableReason(
  fallbackReason: FallbackReason,
  enhancedVoiceOpen: boolean,
): VoiceStartUnavailableReason {
  if (!enhancedVoiceOpen) return 'unavailable';
  if (fallbackReason === 'service') return 'retry-soon';
  if (fallbackReason === 'offline') return 'offline';
  return 'unavailable';
}

export function dispatchVoiceStartUnavailable(reason: VoiceStartUnavailableReason): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<VoiceStartUnavailableReason>(VOICE_START_UNAVAILABLE_EVENT, { detail: reason }),
    );
  } catch {
    // Older browsers without CustomEvent: nothing to show.
  }
}
